import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { sgo, orc } from "@/lib/api";
import { SgoShell } from "@/components/SgoShell";
import { Search, Plus, FolderOpen, X, ClipboardList } from "lucide-react";

const CCT_ATIVA = { nome: "SESVESP", extra: "· FETRAVESP / SEEVISSP", cct_code: "CCT SP 2026/2027", local: "São Paulo-SP 01/01/2026 a 31/12/2027", vigente: true, reajuste: 5.75, piso: 2271.74, peric: 30.00, adic_not: 20.00 };

export default function SgoOrcamentos() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dash, setDash] = useState({ nf_total: 0, lucro: 0, liquido: 0 });

  useEffect(() => {
    orc.list().then(r => setItems(r.data)).catch(() => {});
    sgo.dashboard().then(r => setDash(r.data)).catch(() => {});
  }, []);

  const fmt = (v) => (v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
  const filtered = items.filter(o => !q || o.cliente_nome?.toLowerCase().includes(q.toLowerCase()));

  return (
    <SgoShell activeModule="orc-lista">
      <div className="p-5 md:p-8 fade-up grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
        {/* COLUNA CENTRAL */}
        <div>
          <div className="bg-white rounded-2xl border border-[#B28F4B]/15 shadow-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b-2 border-[#B28F4B]/25">
              <div className="flex items-center gap-2">
                <FolderOpen size={17} className="text-[#B28F4B]"/>
                <h2 className="font-heading text-lg font-bold text-[#1A1D42]">Orçamentos Realizados</h2>
              </div>
              <Link
                to="/sgo/orcamentos/novo"
                data-testid="btn-novo-orcamento-sgo"
                className="bg-gradient-to-br from-[#B28F4B] to-[#8b6f3a] hover:from-[#D4AF37] hover:to-[#B28F4B] text-[#1A1D42] font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-widest inline-flex items-center gap-2 shadow-lg"
              >
                <Plus size={14}/> Novo Orçamento
              </Link>
            </div>

            {/* Filtros */}
            <div className="p-5 border-b border-slate-200 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
                <F label="Buscar por">
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cliente, CNPJ, endereço, número..." className="inp-light"/>
                </F>
                <F label="Campo">
                  <select className="inp-light"><option>Todos os campos</option></select>
                </F>
              </div>
              <F label="Ordenar por">
                <select className="inp-light"><option>Nº Decrescente</option><option>Data</option><option>Cliente</option></select>
              </F>
              <div className="flex flex-wrap gap-2 pt-1">
                <Chip>Todos os Status</Chip>
                <Chip>Todos os Meses</Chip>
                <Chip>Todos os Anos</Chip>
                <button onClick={() => setQ("")} className="text-xs px-3 py-1.5 rounded-full font-semibold text-[#6B6E8F] hover:text-[#1A1D42] border border-slate-300 bg-white inline-flex items-center gap-1">
                  <X size={12}/> Limpar filtros
                </button>
              </div>
            </div>

            {/* Lista / Vazio */}
            {filtered.length === 0 ? (
              <div className="p-16 text-center">
                <ClipboardList size={56} className="mx-auto text-[#B28F4B]/40 mb-3"/>
                <div className="text-[#1A1D42] font-heading text-base mb-1">Nenhum orçamento criado ainda.</div>
                <div className="text-[#6B6E8F] text-sm">Clique em <strong className="text-[#B28F4B]">Novo Orçamento</strong> para começar.</div>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {filtered.map(o => (
                  <Link key={o.id} to={`/orcamentos`} className="grid grid-cols-12 gap-3 px-5 py-4 items-center hover:bg-slate-50 transition">
                    <div className="col-span-6 text-[#1A1D42] font-bold text-sm">{o.cliente_nome}</div>
                    <div className="col-span-2 text-[#6B6E8F] text-xs">{o.uf}</div>
                    <div className="col-span-2 text-[#6B6E8F] text-xs">{o.total_itens || 0} itens</div>
                    <div className="col-span-2 text-right font-mono font-bold text-[#B28F4B]">R$ {fmt(o.valor_total || 0)}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* COLUNA DIREITA — Cards */}
        <aside className="space-y-4">
          {/* Resumo do Orçamento */}
          <div className="bg-gradient-to-br from-[#131532] to-[#1F2350] border border-[#B28F4B]/25 rounded-2xl p-5">
            <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.25em] uppercase mb-4">Resumo do Orçamento</div>
            <div className="bg-[#0A0B1A]/50 border border-[#B28F4B]/20 rounded-xl p-4 mb-3">
              <div className="text-[#A1A4C7] text-[9px] uppercase tracking-widest font-bold text-center">Faturamento Mensal · NF Total</div>
              <div className="text-[#B28F4B] font-mono text-3xl font-bold text-center mt-1">R$ {fmt(dash.nf_total)}</div>
              <div className="text-[#6B6E8F] text-[10px] text-center mt-1">{dash.postos || 0} posto(s) · 0h NF —</div>
            </div>
            <ResumoLinha label="Custo Total" value={`R$ ${fmt(0)}`} color="#B28F4B"/>
            <ResumoLinha label="Tributos s/ NF" value={`R$ ${fmt(dash.nf_total * 0.0865)}`} color="#B28F4B"/>
            <ResumoLinha label="Lucro Estimado" value={`R$ ${fmt(dash.lucro)}`} color="#B28F4B"/>
            <div className="mt-3 pt-3 border-t border-[#B28F4B]/20 bg-emerald-500/10 -mx-5 -mb-5 px-5 py-3 rounded-b-2xl">
              <div className="flex items-center justify-between">
                <div className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Líquido no Caixa</div>
                <div className="text-emerald-400 font-mono text-lg font-bold">R$ {fmt(dash.liquido)}</div>
              </div>
            </div>
          </div>

          {/* Card CCT Ativa (fundo claro) */}
          <div className="bg-white rounded-2xl border border-[#B28F4B]/15 shadow-lg p-5">
            <div className="text-[#1A1D42] font-bold text-lg font-heading">{CCT_ATIVA.nome}</div>
            <div className="text-[#6B6E8F] text-xs mt-0.5">{CCT_ATIVA.extra}</div>
            <div className="text-[#1A1D42] text-sm font-semibold mt-2">{CCT_ATIVA.cct_code}</div>
            <div className="text-[#6B6E8F] text-[11px] mt-1">{CCT_ATIVA.local}</div>
            <div className="flex gap-2 mt-3">
              <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 border border-emerald-300 text-emerald-700 px-2 py-1 rounded-full">Vigente</span>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 border border-emerald-300 text-emerald-700 px-2 py-1 rounded-full">Reajuste {CCT_ATIVA.reajuste.toFixed(2)}%</span>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-[#B28F4B]/30 rounded-xl p-4 mt-4 text-center">
              <div className="text-[#B28F4B] text-[9px] uppercase tracking-widest font-bold">Piso Salarial CCT</div>
              <div className="text-[#B28F4B] font-mono font-bold text-2xl mt-1">R$ {CCT_ATIVA.piso.toFixed(2).replace(".", ",")}</div>
              <div className="text-[#6B6E8F] text-[10px] mt-1">Peric: {CCT_ATIVA.peric.toFixed(2)}% · Adic.Not: {CCT_ATIVA.adic_not.toFixed(2)}%</div>
            </div>
            <button className="mt-3 w-full border-2 border-[#B28F4B]/40 text-[#B28F4B] hover:bg-[#B28F4B]/10 rounded-xl py-2 text-xs font-bold uppercase tracking-widest">
              ↻ Atualizar com nova CCT
            </button>
          </div>

          {/* Encargos + Provisões */}
          <div className="bg-gradient-to-br from-[#131532] to-[#1F2350] border border-[#B28F4B]/25 rounded-2xl p-5">
            <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.25em] uppercase mb-3">Encargos + Provisões s/ Salário</div>
            <Progress label="Encargos" value={36.80} color="#60A5FA"/>
            <Progress label="Provisões" value={36.54} color="#A78BFA"/>
            <div className="mt-3 pt-3 border-t border-[#B28F4B]/20 flex items-center justify-between">
              <div className="text-white text-sm font-bold">Total s/ Salário</div>
              <div className="text-emerald-400 font-mono text-lg font-bold">73,34%</div>
            </div>
          </div>
        </aside>
      </div>
      <style>{`.inp-light { width:100%; background:#F8FAFC; border:1.5px solid #E2E8F0; color:#1A1D42; padding:10px 14px; border-radius:10px; outline:none; font-size:13px; } .inp-light:focus { border-color:#B28F4B; }`}</style>
    </SgoShell>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-widest text-[#6B6E8F] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
function Chip({ children }) {
  return <button className="text-xs px-3 py-1.5 rounded-full font-semibold text-[#6B6E8F] hover:text-[#1A1D42] border border-slate-300 bg-white">{children}</button>;
}
function ResumoLinha({ label, value, color }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#B28F4B]/10">
      <div className="text-white text-xs">{label}</div>
      <div className="font-mono text-sm font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
function Progress({ label, value, color }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-white/80">{label} {value.toFixed(2).replace(".", ",")}%</span>
        <span className="font-mono font-bold" style={{ color }}>{value.toFixed(2).replace(".", ",")}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#0A0B1A]"><div className="h-full rounded-full" style={{ width: `${value}%`, background: color }}/></div>
    </div>
  );
}
